import { HelpButton } from "@/components/help-button";
import { Link } from "@/components/links";
import { Loading } from "@/components/loading";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { useHistoryEvents } from "@/hooks/use-history-events";
import { DAY, WEEK, MONTH } from "@/lib/constants";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { formatNumber, plural, shortenNumber } from "@/lib/utils";
import { useTimeframeTabContext } from "@/providers/timeframe-tab-context";
import {
  DSContractState,
  EmittedArc28EventAndTransaction,
} from "@myth-finance/dualstake-ts-sdk";
import { cx } from "class-variance-authority";
import { Annoyed, ArrowRightLeft } from "lucide-react";
import { useMemo } from "react";

export function prettyTimeframe(timeframe: string): string {
  switch (timeframe) {
    case "1D":
      return "24 hours";
    case "1W":
      return "7 days";
    case "1M":
      return "30 days";
    default:
      return timeframe;
  }
}

function sumSwaps(swapEvents: EmittedArc28EventAndTransaction[]): bigint {
  return swapEvents.reduce((total, event) => {
    return total + (event.argsByName.algo_amount as bigint);
  }, 0n);
}

function sumBuys(swapEvents: EmittedArc28EventAndTransaction[]): bigint {
  return swapEvents.reduce((total, event) => {
    return total + (event.argsByName.asa_amount as bigint);
  }, 0n);
}

export function SwapsCard({ cState, className }: { className?: string; cState: DSContractState }) {
  const {
    error,
    isPending,
    refetch,
    data: appEvents,
  } = useHistoryEvents(cState?.appId);
  const { activeTab, setActiveTab } = useTimeframeTabContext();
  const textColors = getHighlightClasses(cState.asaId, "text");

  const swaps = useMemo(() => {
    if (!appEvents) return null;
    const swapEvents = appEvents.events.filter(
      ({ spec }) => spec.name === "swap"
    );
    const now = Date.now() / 1000;
    const swaps1d = swapEvents.filter(
      ({ outerTransaction: { roundTime: rt } }) => now - Number(rt) <= DAY
    );
    const swaps1w = swapEvents.filter(
      ({ outerTransaction: { roundTime: rt } }) => now - Number(rt) <= WEEK
    );
    const swaps1m = swapEvents.filter(
      ({ outerTransaction: { roundTime: rt } }) => now - Number(rt) <= MONTH
    );
    return {
      "1D": {
        label: "24 hours",
        sold: sumSwaps(swaps1d),
        bought: sumBuys(swaps1d),
        count: swaps1d.length,
      },
      "1W": {
        label: "7 days",
        sold: sumSwaps(swaps1w),
        bought: sumBuys(swaps1w),
        count: swaps1w.length,
      },
      "1M": {
        label: "30 days",
        sold: sumSwaps(swaps1m),
        bought: sumBuys(swaps1m),
        count: swaps1m.length,
      },
      ALL: {
        label: "n o m a t t e r",
        sold: sumSwaps(swapEvents),
        bought: sumBuys(swapEvents),
        count: swapEvents.length,
      },
    };
  }, [appEvents]);

  return (
    <Card className={cx("min-w-[300px] relative self-stretch", className)}>
      <CardHeader>
        <CardTitle className="opacity-0 animate-fade-in-3">
          <ArrowRightLeft className="relative -top-[2px] inline" />{" "}{cState.asaUnitName} Buy Pressure
        </CardTitle>
      </CardHeader>
      <CardContent className="opacity-0 animate-fade-in-3">
        <WithErrorAndLoading
          mini={true}
          loading={false}
          error={error?.message}
          retry={refetch}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full mb-3 -mt-2 grid-cols-4">
              <TabsTrigger value="1D">24H</TabsTrigger>
              <TabsTrigger value="1W">1W</TabsTrigger>
              <TabsTrigger value="1M">1M</TabsTrigger>
              <TabsTrigger value="ALL">ALL</TabsTrigger>
            </TabsList>
            {Object.entries(swaps ?? {}).map(
              ([timeframe, { sold, bought, count }]) => (
                <TabsContent key={timeframe} value={timeframe}>
                  {sold ? (
                    <>
                      <p className="text-left text-muted-foreground mb-1 text-sm">
                        In the{" "}
                        {timeframe === "ALL" ? (
                          <>{cState.lstName} lifetime</>
                        ) : (
                          <>past {prettyTimeframe(timeframe)}</>
                        )}
                      </p>
                      <div className="text-3xl ">
                        {formatNumber(sold)}{" "}
                        <span className="font-light">ALGO</span>
                      </div>

                      <p className="text-muted-foreground my-1 text-center  text-sm">
                        {timeframe === "ALL" ? "has been" : "was"} swapped to
                      </p>
                      <div className="text-3xl text-right">
                        {shortenNumber(
                          Number(bought) / 10 ** cState.asaDecimals
                        )}{" "}
                        <span className="font-light">{cState.asaUnitName}</span>
                      </div>
                      <div className="flex justify-between">
                        <HelpButton title={`What is this?`}>
                          <div><span className={textColors}>{cState.lstName}</span> participates in Algorand consensus and receives ALGO rewards.</div>
                          <div>These rewards are automatically swapped into <span className={textColors}>{cState.asaUnitName}</span> via Tinyman.</div>
                          <div className="text-muted-foreground">The purchased {cState.asaUnitName} is incorporated into the value of each circulating {cState.lstName}.</div>
                          <div className="text-muted-foreground">Read more about swapping <Link to="https://docs.myth.finance/dualSTAKE/rewards">here</Link>.</div>
                        </HelpButton>
                        <p className="text-muted-foreground text-right -mb-1 text-sm">
                          over {count} {plural("swap", count)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col items-center gap-2 py-9">
                        <Annoyed
                          size={40}
                          className="text-muted-foreground/70 mb-2"
                        />
                        <span className="text-muted-foreground">
                          No swaps { timeframe === "ALL" ? <>at all</> : <>in the past {prettyTimeframe(timeframe)}</>}
                        </span>
                      </div>
                    </>
                  )}
                </TabsContent>
              )
            )}
          </Tabs>
          {!swaps ? (
            isPending ? (
              <Loading className="my-16 py-2" noLogo={true} />
            ) : (
              <div className="text-center">N/A</div>
            )
          ) : null}
        </WithErrorAndLoading>
      </CardContent>
      <ArrowRightLeft
        size={192}
        strokeWidth="1.25px"
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
      />
    </Card>
  );
}
